import { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import maplibregl from 'maplibre-gl'
import {
  selectTerritoryBoundariesById,
  selectTerritorySummaries,
} from '@user/store/territory'
import { useD2DTerritoriesEnabled } from '@user/store/tenantFeatures/hooks/useD2DTerritoriesEnabled'
import {
  selectCollisionDetectionEnabled,
  selectContactLocations,
  selectSalespersonFilterOptions,
  selectSalespersonFilterSelection,
  selectSearchAreaBoundary,
} from '@user/store/map'
import { ringsToGeoJsonGeometry } from '@user/components/HereMap/hooks/boundaryUtils'
import {
  buildIconSvg,
  getVariantFromEntityType,
} from '@user/components/HereMap/utils/createMarker'
import { EntityLocationDTO } from '@user/store/map/typings'
import { MapPinTypeVariant } from '@user/components/HereMap/MapPin/types'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'
import { useSalesPersonFilterEnabled } from '@user/store/tenantFeatures/hooks/useSalesPersonFilterEnabled'
import { DEFAULT_TERRITORY_COLOR } from '@user/store/territory/constants'
import {
  CL_H,
  CL_W,
  currentLocationSvg,
} from '@user/components/HereMap/utils/pinSvg'
import {
  computeAllowOverlap,
  featureToLocation,
  getIconKey,
  locationToFeature,
} from './useMapLibreMarkers.utils'
import { isPinVisibleForSalespersonFilter } from './useSalespersonFilter.utils'
import {
  TERRITORY_DRAFT_CLOSING_LAYER,
  TERRITORY_DRAFT_FILL_LAYER,
  TERRITORY_DRAFT_LINE_LAYER,
  TERRITORY_DRAFT_SOURCE,
} from './useTerritoryDraw'
import {
  ACTIVE_PIN_LAYER,
  ACTIVE_PIN_SOURCE,
  CONTACT_PINS_LAYER,
  CONTACT_PINS_SOURCE,
  TERRITORY_FILL_LAYER,
  TERRITORY_LINE_LAYER,
  TERRITORY_SOURCE,
} from './mapLayerIds'

// ─── Source / layer IDs ───────────────────────────────────────────────────────

// Imported from mapLayerIds, and re-exported so existing importers keep their path: the ids
// have to be nameable by hooks that only query a layer (useTerritoryInteraction), and this
// module cannot be imported without maplibre-gl coming with it.
export {
  CONTACT_PINS_SOURCE,
  CONTACT_PINS_LAYER,
  ACTIVE_PIN_SOURCE,
  ACTIVE_PIN_LAYER,
  TERRITORY_SOURCE,
  TERRITORY_FILL_LAYER,
}

// Searched-area boundary polygon (zip/county/city/state outline). Drawn beneath
// the pins so they stay clickable on top of the fill. See useMapLibreBoundary.
const SEARCH_BOUNDARY_SOURCE = 'search-boundary'
const SEARCH_BOUNDARY_FILL_LAYER = 'search-boundary-fill'
const SEARCH_BOUNDARY_LINE_LAYER = 'search-boundary-line'

// D2D sales territories (WWM-17508). Drawn at the very bottom -- beneath the search
// boundary and well beneath the pins -- because territories are always visible and
// permanent, unlike the transient search highlight. Solid lines, where the search
// boundary is dashed: with both on screen at once that is what tells them apart.
// The fill DOES answer hover and click (useTerritoryInteraction), but never at a pin's
// expense: that hook checks the pin layers first and stands aside, because an always-on
// polygon under every pin must not win a tap meant for a house.
// Imported, not restated: a draft id that drifts out of sync with useTerritoryDraw would fail
// silently, leaving the draft to disappear on a base-map switch exactly as it used to. The
// territory ids come from mapLayerIds for the same reason.

// The custom sources/layers carried across a base-map style switch.
const CARRIED_SOURCE_IDS = [
  TERRITORY_SOURCE,
  // The in-progress lasso. Without it, switching base map mid-draw destroyed the draft source
  // and nothing re-created it -- drawing kept working but drew nothing visible.
  TERRITORY_DRAFT_SOURCE,
  SEARCH_BOUNDARY_SOURCE,
  CONTACT_PINS_SOURCE,
  ACTIVE_PIN_SOURCE,
] as const
const CARRIED_LAYER_IDS: string[] = [
  TERRITORY_FILL_LAYER,
  TERRITORY_LINE_LAYER,
  TERRITORY_DRAFT_FILL_LAYER,
  TERRITORY_DRAFT_LINE_LAYER,
  TERRITORY_DRAFT_CLOSING_LAYER,
  SEARCH_BOUNDARY_FILL_LAYER,
  SEARCH_BOUNDARY_LINE_LAYER,
  CONTACT_PINS_LAYER,
  ACTIVE_PIN_LAYER,
]

// A maplibre TransformStyleFunction that carries the imperatively-added pin and
// boundary sources (with their current GeoJSON data) and layers from the
// current style into the next one during a setStyle() diff. Without it, the
// diff removes them (they're absent from the new base style) and they flash out
// and back in once the styledata effect re-adds + re-rasterizes them. Both
// setStyle call sites (LayerControls and useMapLibreInstance) MUST use this so
// neither one tears them down. See SH-909.
export const preservePinLayers: maplibregl.TransformStyleFunction = (
  previousStyle,
  nextStyle,
) => {
  if (!previousStyle) return nextStyle

  const sources = { ...nextStyle.sources }
  for (const id of CARRIED_SOURCE_IDS) {
    if (previousStyle.sources[id]) {
      sources[id] = previousStyle.sources[id]
    }
  }

  // Preserve the original stacking order of the carried layers (boundary below
  // pins). filter() keeps previousStyle.layers' order, and appending after the
  // base-map layers keeps everything on top of the base map.
  const carriedLayers = previousStyle.layers.filter((layer) =>
    CARRIED_LAYER_IDS.includes(layer.id),
  )

  return {
    ...nextStyle,
    sources,
    layers: [...nextStyle.layers, ...carriedLayers],
  }
}

async function rasterizeIcon(
  map: maplibregl.Map,
  iconKey: string,
  svg: string,
  w: number,
  h: number,
): Promise<void> {
  if (map.hasImage(iconKey)) return
  const blob = new Blob([svg], { type: 'image/svg+xml' })
  const url = URL.createObjectURL(blob)
  try {
    // map.addImage reads naturalWidth/naturalHeight from HTMLImageElement, which
    // for SVGs is the SVG's own declared size — not the img element's width/height
    // attributes. On HiDPI screens this caused pins to render at 1/dpr of the
    // intended CSS size. Drawing onto a canvas forces the SVG to fill physW×physH
    // and gives MapLibre a bitmap with the correct dimensions.
    const dpr = window.devicePixelRatio || 1
    const physW = Math.round(w * dpr)
    const physH = Math.round(h * dpr)
    const img = new Image()
    img.src = url
    await img.decode()
    const canvas = document.createElement('canvas')
    canvas.width = physW
    canvas.height = physH
    const ctx = canvas.getContext('2d')!
    ctx.drawImage(img, 0, 0, physW, physH)
    const imageData = ctx.getImageData(0, 0, physW, physH)
    if (!map.hasImage(iconKey)) {
      map.addImage(iconKey, imageData, { pixelRatio: dpr })
    }
  } finally {
    URL.revokeObjectURL(url)
  }
}

// ─── Layer management ─────────────────────────────────────────────────────────

const SYMBOL_LAYOUT = {
  'icon-image': ['get', 'iconKey'],
  'icon-offset': ['get', 'iconOffset'],
  'icon-anchor': 'center',
} as any

// The overlay always renders its single feature regardless of the collision
// toggle: it must never be culled by the placement pass, and it must never push
// the contact pins beneath it around. icon-ignore-placement keeps it out of the
// collision index entirely.
const ACTIVE_SYMBOL_LAYOUT = {
  ...SYMBOL_LAYOUT,
  'icon-allow-overlap': true,
  'icon-ignore-placement': true,
} as any

function ensureSource(map: maplibregl.Map, id: string): boolean {
  if (map.getSource(id)) return false
  map.addSource(id, {
    type: 'geojson',
    data: { type: 'FeatureCollection', features: [] },
  })
  return true
}

function ensureLayer(
  map: maplibregl.Map,
  layer: maplibregl.AddLayerObject,
): boolean {
  if (map.getLayer(layer.id)) return false
  map.addLayer(layer)
  return true
}

/** One source holds every territory, so the colour is read per feature. */
const TERRITORY_COLOR: maplibregl.ExpressionSpecification = [
  'coalesce',
  ['get', 'colorHex'],
  DEFAULT_TERRITORY_COLOR,
]

/**
 * Faint on purpose. Territories are always visible, so anything heavier muddies the map and
 * makes the pins -- the thing reps actually click -- hard to read.
 *
 * Hover and selection lift it through FEATURE STATE rather than by re-setting the source data:
 * setData on every pointer move would rebuild every polygon on the map to shade one of them.
 * The states are written by useTerritoryInteraction, keyed on the feature id, which is the
 * territory id.
 */
const TERRITORY_FILL_OPACITY: maplibregl.ExpressionSpecification = [
  'case',
  ['boolean', ['feature-state', 'selected'], false],
  0.3,
  ['boolean', ['feature-state', 'hover'], false],
  0.2,
  0.1,
]

/**
 * The selected territory keeps a heavier outline, so the shape the open bubble is describing
 * stays identifiable once the cursor has moved off it to read the numbers.
 */
const TERRITORY_LINE_WIDTH: maplibregl.ExpressionSpecification = [
  'case',
  ['boolean', ['feature-state', 'selected'], false],
  4,
  2,
]

function addSourcesAndLayers(map: maplibregl.Map): boolean {
  const added = [
    // Boundary first so it renders BENEATH the pins (added later, on top).
    ensureSource(map, TERRITORY_SOURCE),
    ensureLayer(map, {
      id: TERRITORY_FILL_LAYER,
      type: 'fill',
      source: TERRITORY_SOURCE,
      paint: {
        'fill-color': TERRITORY_COLOR,
        'fill-opacity': TERRITORY_FILL_OPACITY,
      },
    }),
    ensureLayer(map, {
      id: TERRITORY_LINE_LAYER,
      type: 'line',
      source: TERRITORY_SOURCE,
      paint: {
        'line-color': TERRITORY_COLOR,
        'line-width': TERRITORY_LINE_WIDTH,
      },
    }),

    ensureSource(map, SEARCH_BOUNDARY_SOURCE),
    ensureLayer(map, {
      id: SEARCH_BOUNDARY_FILL_LAYER,
      type: 'fill',
      source: SEARCH_BOUNDARY_SOURCE,
      paint: { 'fill-color': '#ff0000', 'fill-opacity': 0.05 },
    }),
    ensureLayer(map, {
      id: SEARCH_BOUNDARY_LINE_LAYER,
      type: 'line',
      source: SEARCH_BOUNDARY_SOURCE,
      paint: {
        'line-color': '#ff0000',
        'line-width': 2,
        'line-dasharray': [2, 4],
      },
    }),

    ensureSource(map, CONTACT_PINS_SOURCE),
    ensureLayer(map, {
      id: CONTACT_PINS_LAYER,
      type: 'symbol',
      source: CONTACT_PINS_SOURCE,
      layout: SYMBOL_LAYOUT,
    }),

    // Added after the contact-pins layer so it renders on top. The active pin is
    // NOT removed from the contact-pins layer; the overlay's selected icon shares
    // the same anchor/size and is fully opaque, so it covers its default twin.
    ensureSource(map, ACTIVE_PIN_SOURCE),
    ensureLayer(map, {
      id: ACTIVE_PIN_LAYER,
      type: 'symbol',
      source: ACTIVE_PIN_SOURCE,
      layout: ACTIVE_SYMBOL_LAYOUT,
    }),
  ]

  return added.some(Boolean)
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

interface UseMapLibreMarkersParams {
  map: maplibregl.Map | null
  onMarkerClick: (markerData: EntityLocationDTO) => void
  onMarkerHover: (
    markerData: EntityLocationDTO | null,
    immediate?: boolean,
  ) => void
  activePin: EntityLocationDTO | null
}

export function useMapLibreMarkers({
  map,
  onMarkerClick,
  onMarkerHover,
  activePin,
}: UseMapLibreMarkersParams) {
  const isD2dEnabled = useD2DKnockEnabled()
  const salesPersonFilterEnabled = useSalesPersonFilterEnabled()
  const contactLocations = useSelector(selectContactLocations)
  const collisionDetectionEnabled = useSelector(selectCollisionDetectionEnabled)
  const searchAreaBoundary = useSelector(selectSearchAreaBoundary)
  const territorySummaries = useSelector(selectTerritorySummaries)
  const territoryBoundariesById = useSelector(selectTerritoryBoundariesById)
  const territoriesEnabled = useD2DTerritoriesEnabled()
  const salespersonFilterSelection = useSelector(
    selectSalespersonFilterSelection,
  )
  const salespersonFilterOptions = useSelector(selectSalespersonFilterOptions)

  // Incremented when a styledata event finds the pin sources/layers gone, so
  // Effect 2 re-runs and repopulates pin data after a map style switch
  // (Map → Satellite → Terrain etc.).
  const [styleVersion, setStyleVersion] = useState(0)

  // True once the map has reached its max zoom — at that point all pins must
  // show regardless of the Pin Density setting (SH-919), since there's no
  // further zoom level to separate overlapping pins into.
  const [isAtMaxZoom, setIsAtMaxZoom] = useState(false)

  const onMarkerClickRef = useRef(onMarkerClick)
  const onMarkerHoverRef = useRef(onMarkerHover)
  useEffect(() => {
    onMarkerClickRef.current = onMarkerClick
  }, [onMarkerClick])
  useEffect(() => {
    onMarkerHoverRef.current = onMarkerHover
  }, [onMarkerHover])

  // Effect 1: add sources + layers, wire layer events, handle style reloads
  useEffect(() => {
    if (!map) return

    addSourcesAndLayers(map)

    const onStyleData = () => {
      if (!addSourcesAndLayers(map)) return
      setStyleVersion((v) => v + 1)
    }
    map.on('styledata', onStyleData)

    const handleLayerClick = (e: maplibregl.MapLayerMouseEvent) => {
      const eventTime = (e.originalEvent as MouseEvent | TouchEvent).timeStamp
      if (eventTime === lastLayerClickTime) return
      lastLayerClickTime = eventTime

      const feature = e.features?.[0]
      if (!feature) return
      onMarkerClickRef.current(
        featureToLocation(feature.properties as Record<string, unknown>),
      )
    }

    let lastLayerClickTime = -1

    // Track the last hovered pinId so mousemove only fires onMarkerHover once
    // per pin entry — not on every pixel of movement. This prevents the rapid
    // re-render cascade that caused flickering and repeated backend calls.
    let hoveredPinId: string | null = null

    const handleMouseMove = (e: maplibregl.MapLayerMouseEvent) => {
      const feature = e.features?.[0]
      if (!feature) return
      const location = featureToLocation(
        feature.properties as Record<string, unknown>,
      )
      if (location.pinId === hoveredPinId) return
      hoveredPinId = location.pinId
      map.getCanvas().style.cursor = 'pointer'
      onMarkerHoverRef.current(location)
    }
    const handleMouseLeave = () => {
      hoveredPinId = null
      onMarkerHoverRef.current(null)
      map.getCanvas().style.cursor = 'default'
    }

    // On touch devices, mousemove events may not fire from touch input so
    // onMarkerHover never gets called. Wire touchstart/touchend to the hover
    // path so tap-and-hold opens the bubble while holding and closes on
    // release — matching master's expected behaviour (SH-877).
    // Quick taps additionally fire a click event which goes through
    // handleLayerClick → setTooltipHovered(true) so the bubble stays open.
    // Touch bypasses the 2s hover-open delay — a tap opens now.
    const handleTouchStart = (
      e: maplibregl.MapTouchEvent & {
        features?: maplibregl.MapGeoJSONFeature[]
      },
    ) => {
      const feature = e.features?.[0]
      if (!feature) return
      const location = featureToLocation(
        feature.properties as Record<string, unknown>,
      )
      hoveredPinId = location.pinId
      map.getCanvas().style.cursor = 'pointer'
      onMarkerHoverRef.current(location, true)
    }
    const handleTouchEnd = () => {
      hoveredPinId = null
      onMarkerHoverRef.current(null)
      map.getCanvas().style.cursor = 'default'
    }

    map.on('click', CONTACT_PINS_LAYER, handleLayerClick)
    map.on('click', ACTIVE_PIN_LAYER, handleLayerClick)
    map.on('touchstart', CONTACT_PINS_LAYER, handleTouchStart)
    map.on('touchstart', ACTIVE_PIN_LAYER, handleTouchStart)
    map.on('touchend', CONTACT_PINS_LAYER, handleTouchEnd)
    map.on('touchend', ACTIVE_PIN_LAYER, handleTouchEnd)
    map.on('mousemove', CONTACT_PINS_LAYER, handleMouseMove)
    map.on('mousemove', ACTIVE_PIN_LAYER, handleMouseMove)
    map.on('mouseleave', CONTACT_PINS_LAYER, handleMouseLeave)
    map.on('mouseleave', ACTIVE_PIN_LAYER, handleMouseLeave)

    return () => {
      map.off('styledata', onStyleData)
      map.off('click', CONTACT_PINS_LAYER, handleLayerClick)
      map.off('click', ACTIVE_PIN_LAYER, handleLayerClick)
      map.off('touchstart', CONTACT_PINS_LAYER, handleTouchStart)
      map.off('touchstart', ACTIVE_PIN_LAYER, handleTouchStart)
      map.off('touchend', CONTACT_PINS_LAYER, handleTouchEnd)
      map.off('touchend', ACTIVE_PIN_LAYER, handleTouchEnd)
      map.off('mousemove', CONTACT_PINS_LAYER, handleMouseMove)
      map.off('mousemove', ACTIVE_PIN_LAYER, handleMouseMove)
      map.off('mouseleave', CONTACT_PINS_LAYER, handleMouseLeave)
      map.off('mouseleave', ACTIVE_PIN_LAYER, handleMouseLeave)
    }
  }, [map])

  // Effect 2: reconcile contactLocations → contact-pins GeoJSON source
  useEffect(() => {
    if (!map) return
    let cancelled = false

    const run = async () => {
      const renderableLocations = contactLocations.filter((loc) => {
        const variant = getVariantFromEntityType(loc.entityType)
        if (variant === MapPinTypeVariant.CurrentLocation) return false
        if (!loc.multitenant && loc.count > 1) return false
        // KnockStatus-type pins with no active knock status (null color) are
        // bad/orphaned data — no entity, nothing to show. Hide them (SH-861).
        if (
          loc.entityType?.toLowerCase() === 'knockstatus' &&
          !loc.knockStatusSettingColor
        )
          return false
        if (
          salesPersonFilterEnabled &&
          !isPinVisibleForSalespersonFilter(
            loc,
            salespersonFilterSelection,
            salespersonFilterOptions,
          )
        )
          return false
        return true
      })

      // Precompute SVG data for all locations — both default and selected
      // variants. Both are baked into the feature properties so the layer's
      // icon-image case expression can swap to the selected version on hover
      // without a source update or a layer swap.
      const pinData = renderableLocations.map((loc) => {
        const variant = getVariantFromEntityType(loc.entityType)
        const iconKey = getIconKey(loc, false, isD2dEnabled)
        const selectedIconKey = getIconKey(loc, true, isD2dEnabled)
        const { svg, w, h, ax, ay } = buildIconSvg(
          loc,
          variant,
          false,
          isD2dEnabled,
        )
        const { svg: selectedSvg } = buildIconSvg(
          loc,
          variant,
          true,
          isD2dEnabled,
        )
        return { loc, iconKey, selectedIconKey, svg, selectedSvg, w, h, ax, ay }
      })

      // Rasterize unique icon variants (both default and selected) not yet in
      // the sprite
      const seen = new Set<string>()
      const rasterizeOps: Promise<void>[] = []
      for (const {
        iconKey,
        selectedIconKey,
        svg,
        selectedSvg,
        w,
        h,
      } of pinData) {
        if (!map.hasImage(iconKey) && !seen.has(iconKey)) {
          seen.add(iconKey)
          rasterizeOps.push(rasterizeIcon(map, iconKey, svg, w, h))
        }
        if (!map.hasImage(selectedIconKey) && !seen.has(selectedIconKey)) {
          seen.add(selectedIconKey)
          rasterizeOps.push(
            rasterizeIcon(map, selectedIconKey, selectedSvg, w, h),
          )
        }
      }
      await Promise.all(rasterizeOps)

      if (cancelled) return

      const source = map.getSource(CONTACT_PINS_SOURCE) as
        | maplibregl.GeoJSONSource
        | undefined
      source?.setData({
        type: 'FeatureCollection',
        features: pinData.map(
          ({ loc, iconKey, selectedIconKey, w, h, ax, ay }) =>
            locationToFeature(
              loc,
              iconKey,
              selectedIconKey,
              w / 2 - ax,
              h / 2 - ay,
            ),
        ),
      })
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    map,
    contactLocations,
    isD2dEnabled,
    styleVersion,
    salesPersonFilterEnabled,
    salespersonFilterSelection,
    salespersonFilterOptions,
  ])

  // Effect 3: render the active pin on the dedicated overlay source.
  //
  // The ONLY map mutation on hover is a setData of one feature on the overlay
  // source. The contact-pins layer is never touched — no setFilter, no
  // setLayoutProperty — so it is never re-laid-out and the surrounding pins
  // can't blink (the bug behind SH-824 and its first fix attempt: any change to
  // a *layout* property, including a data-driven icon-image, forces MapLibre to
  // re-run symbol layout + collision placement for the whole layer).
  //
  // The active pin stays in the contact-pins layer too; the overlay's selected
  // icon (same anchor/size, fully opaque, only the colour differs) is drawn on
  // top of its default twin. styleVersion is a dep so the overlay is restored
  // after a base-map style switch, which recreates the (empty) overlay source.
  useEffect(() => {
    if (!map) return
    let cancelled = false

    const run = async () => {
      const source = map.getSource(ACTIVE_PIN_SOURCE) as
        | maplibregl.GeoJSONSource
        | undefined
      if (!source) return

      const empty = { type: 'FeatureCollection' as const, features: [] }
      if (!activePin) {
        source.setData(empty)
        return
      }
      const variant = getVariantFromEntityType(activePin.entityType)
      if (variant === MapPinTypeVariant.CurrentLocation) {
        source.setData(empty)
        return
      }
      // SH-2168: a pin can become active (clicked/hovered) before its owner
      // is deselected in the salesperson filter — this overlay is separate
      // from the contact-pins layer Effect 2 filters, so it must re-check
      // visibility itself or a deselected pin stays visible/actionable.
      if (
        salesPersonFilterEnabled &&
        !isPinVisibleForSalespersonFilter(
          activePin,
          salespersonFilterSelection,
          salespersonFilterOptions,
        )
      ) {
        source.setData(empty)
        return
      }

      const selectedIconKey = getIconKey(activePin, true, isD2dEnabled)
      const { svg, w, h, ax, ay } = buildIconSvg(
        activePin,
        variant,
        true,
        isD2dEnabled,
      )
      await rasterizeIcon(map, selectedIconKey, svg, w, h)
      if (cancelled) return

      // Both icon keys are the selected key so the overlay layer's default
      // icon-image (['get','iconKey']) renders the highlighted variant.
      source.setData({
        type: 'FeatureCollection',
        features: [
          locationToFeature(
            activePin,
            selectedIconKey,
            selectedIconKey,
            w / 2 - ax,
            h / 2 - ay,
          ),
        ],
      })
    }

    run()
    return () => {
      cancelled = true
    }
  }, [
    map,
    activePin,
    isD2dEnabled,
    styleVersion,
    salesPersonFilterEnabled,
    salespersonFilterSelection,
    salespersonFilterOptions,
  ])

  // Track whether the map is at its max zoom so Effect 4 can force pins to
  // show regardless of the Pin Density setting (SH-919). Uses zoomend rather
  // than zoom — zoom fires continuously during a gesture/animation, and this
  // only needs to be correct once the zoom settles.
  useEffect(() => {
    if (!map) return
    const updateIsAtMaxZoom = () => {
      setIsAtMaxZoom(map.getZoom() >= map.getMaxZoom())
    }
    updateIsAtMaxZoom()
    map.on('zoomend', updateIsAtMaxZoom)
    return () => {
      map.off('zoomend', updateIsAtMaxZoom)
    }
  }, [map])

  // Effect 4: sync Pin Density (collision detection) toggle → icon-allow-overlap.
  // styleVersion is a dep so the setting is re-applied after a base-map style
  // switch — otherwise the re-added contact-pins layer reverts to MapLibre's
  // default (icon-allow-overlap: false = collision ON) and pins start culling
  // ("density") on view change even though collision detection is off.
  // At max zoom, all pins always show regardless of the Pin Density setting.
  useEffect(() => {
    if (!map) return
    const allowOverlap = computeAllowOverlap(
      collisionDetectionEnabled,
      isAtMaxZoom,
    )
    if (map.getLayer(CONTACT_PINS_LAYER)) {
      map.setLayoutProperty(
        CONTACT_PINS_LAYER,
        'icon-allow-overlap',
        allowOverlap,
      )
      map.setLayoutProperty(
        CONTACT_PINS_LAYER,
        'symbol-sort-key',
        allowOverlap ? undefined : ['get', 'sortKey'],
      )
    }
  }, [map, collisionDetectionEnabled, isAtMaxZoom, styleVersion])

  // Effect 5: searched-area boundary polygon → search-boundary source.
  // styleVersion is a dep so the polygon is restored after a base-map style
  // switch recreates the (empty) source. Framing is done by the search pan, so
  // this effect never moves the camera.
  useEffect(() => {
    if (!map) return
    const source = map.getSource(SEARCH_BOUNDARY_SOURCE) as
      | maplibregl.GeoJSONSource
      | undefined
    if (!source) return

    if (!searchAreaBoundary?.rings?.length) {
      source.setData({ type: 'FeatureCollection', features: [] })
      return
    }

    source.setData({
      type: 'Feature',
      geometry: ringsToGeoJsonGeometry(searchAreaBoundary.rings),
      properties: {},
    })
  }, [map, searchAreaBoundary, styleVersion])

  // Effect 5b: D2D territory polygons → territory source (WWM-17508).
  //
  // getSource reaches through map.style, which a removed map no longer has, so this is guarded
  // like the territory interaction hook: the map view is a dialog that this feature now closes
  // programmatically, and a store update landing in the same commit as the teardown would
  // otherwise throw on a dead map.
  // styleVersion is a dep for the same reason as Effect 5: a base-map style switch
  // recreates the source empty, so the data has to be re-set afterwards.
  //
  // Only territories whose geometry has actually been fetched are drawn. The rest are
  // known (name, colour, bounds come from the summary call) but not yet on screen —
  // the viewport-driven fetch fills them in, and a half-populated cache renders the
  // part it has rather than waiting for all of them.
  useEffect(() => {
    if (!map || (map as unknown as { _removed?: boolean })._removed) return

    const source = map.getSource(TERRITORY_SOURCE) as
      | maplibregl.GeoJSONSource
      | undefined
    if (!source) return

    if (!territoriesEnabled) {
      source.setData({ type: 'FeatureCollection', features: [] })
      return
    }

    const features = territorySummaries
      .filter((territory) => territoryBoundariesById[territory.id])
      .map((territory) => ({
        type: 'Feature' as const,
        id: territory.id,
        geometry: territoryBoundariesById[territory.id],
        properties: {
          territoryId: territory.id,
          name: territory.name,
          colorHex: territory.colorHex,
        },
      }))

    source.setData({ type: 'FeatureCollection', features })
  }, [
    map,
    territoriesEnabled,
    territorySummaries,
    territoryBoundariesById,
    styleVersion,
  ])

  // Effect 6: current location DOM marker — pulsing blue dot via geolocation watcher
  const currentLocationMarkerRef = useRef<maplibregl.Marker | null>(null)
  const watchIdRef = useRef<number | null>(null)
  const lastKnownPositionRef = useRef<{ lat: number; lng: number } | null>(null)

  useEffect(() => {
    if (!map || !('geolocation' in navigator)) return

    const createOrUpdate = (lat: number, lng: number) => {
      lastKnownPositionRef.current = { lat, lng }
      if (currentLocationMarkerRef.current) {
        currentLocationMarkerRef.current.setLngLat([lng, lat])
        return
      }
      const el = document.createElement('div')
      el.style.width = `${CL_W}px`
      el.style.height = `${CL_H}px`
      el.innerHTML = currentLocationSvg()

      currentLocationMarkerRef.current = new maplibregl.Marker({
        element: el,
        anchor: 'center',
      })
        .setLngLat([lng, lat])
        .addTo(map)
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => createOrUpdate(pos.coords.latitude, pos.coords.longitude),
      (err) => console.error('Geolocation watchPosition error:', err),
      { enableHighAccuracy: true, maximumAge: 0, timeout: 20000 },
    )

    return () => {
      if (watchIdRef.current !== null) {
        navigator.geolocation.clearWatch(watchIdRef.current)
        watchIdRef.current = null
      }
      currentLocationMarkerRef.current?.remove()
      currentLocationMarkerRef.current = null
    }
  }, [map])

  return { lastKnownPositionRef }
}
